<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import z from "zod"
import { LIST_ALBUMS_QUERY_KEY, useUpdateAlbumSort } from "@/api"
import { Alert, BaseModal, Button, Card, Icon, Select } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { AlbumSortModalKey } from "@/symbols"
import { PredefinedSortOrder } from "@/types"
import { useToast } from "../ui/ToastReka/useToast"

const { t } = useTranslation()

const { toggle } = useModal(AlbumSortModalKey)

const eventVaultStore = useEventVaultStore()

const { success, error } = useToast()

const queryClient = useQueryClient()

const validationSchema = toTypedSchema(z.object({
  sort: z.nativeEnum(PredefinedSortOrder, { message: t("album_sort_modal.validation.invalid_sort") }),
}))

const { values } = useForm({
  validationSchema,
  initialValues: {
    sort: PredefinedSortOrder.TitleAsc,
  },
})

const sortItems = [
  { label: t("album_sort_modal.sort_options.title_asc"), value: PredefinedSortOrder.TitleAsc },
  { label: t("album_sort_modal.sort_options.title_desc"), value: PredefinedSortOrder.TitleDesc },
  { label: t("album_sort_modal.sort_options.date_latest"), value: PredefinedSortOrder.AlbumDateNewest },
  { label: t("album_sort_modal.sort_options.date_oldest"), value: PredefinedSortOrder.AlbumDateOldest },
]

const { mutate, isPending } = useUpdateAlbumSort()

function onConfirm() {
  if (!eventVaultStore.eventVaultId || !values.sort) {
    error()
    return
  }

  mutate({ eventVaultId: eventVaultStore.eventVaultId, sort: values.sort }, {
    async onSuccess() {
      success({
        title: t("album_sort_modal.success_title"),
        description: t("album_sort_modal.success_body"),
      })
      await queryClient.invalidateQueries({ queryKey: [LIST_ALBUMS_QUERY_KEY] })
      toggle()
    },
    onError() {
      error()
    },
  })
}
</script>

<template>
  <BaseModal :name="AlbumSortModalKey">
    <Card>
      <template #header>
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-xl font-comfortaa font-bold">
            {{ t("album_sort_modal.title") }}
          </h2>
          <Button variant="flat" @click="toggle">
            <Icon icon="Close" />
            <span class="sr-only">{{ t("album_sort_modal.close_sr") }}</span>
          </Button>
        </div>
      </template>

      <p class="mb-4">
        {{ t("album_sort_modal.message") }}
      </p>

      <form @submit.prevent>
        <div class="flex flex-col gap-5">
          <Select name="sort" :items="sortItems" />

          <Alert variant="danger" :closable="false">
            <h3 class="font-bold mb-2">
              {{ t('album_sort_modal.warning_title') }}
            </h3>
            <p>{{ t('album_sort_modal.warning_body') }}</p>
          </Alert>

          <div class="flex justify-end gap-3">
            <Button variant="tertiary" :disabled="isPending" @click="toggle">
              {{ t("cancel") }}
            </Button>
            <Button :disabled="isPending" @click="onConfirm">
              {{ t("confirm") }}
              <Icon v-if="isPending" icon="Loading" />
            </Button>
          </div>
        </div>
      </form>
    </Card>
  </BaseModal>
</template>
